import React, { useRef, useEffect, useState } from "react";
import ReactPlayer from "react-player"; /* dependency */
import JsonSpeedViewer, { keyCompare } from "./JsonSpeedViewer";
import { CustomLabelRenderer } from "./CustomLabelRenderer";
import { VideoModules } from "./VideoModules";
import { Tab, Tabs } from "@blueprintjs/core";
import ResponsiveCanvas from "../../components/ResponsiveCanvas";
import { useMephistoReview } from "../../shims/mephisto-review-hook";
import { useUploadedDataStore } from "../../stores/UploadedDataStore";
import ErrorPane from "../../components/ErrorPane";

import "./VideoDetail.scss";
import useBBoxes from "./useBBoxes";
import { VideoControls } from "./VideoControls";
import useStateWithUrlParam from "../../hooks/useStateWithUrlParam";
import useAtomWithUrlParam from "../../hooks/useAtomWithUrlParam";
import { versionAtom, versionAtomDefault } from "../../atoms";

export default function VideoDetail({ id }) {
    const [version] = useAtomWithUrlParam(versionAtom, versionAtomDefault);
    const {
        data: item,
        isLoading,
        error,
    } = useMephistoReview({taskId: id});

    const [progress, setProgress] = useState(0);
    const [progressCheckpoint, setProgressCheckpoint] = useState(null);
    const [duration, setDuration] = useState(0);
    const [playing, setPlaying] = useState(false);
    const [playerReady, setPlayerReady] = useState(false);
    const [, setBuffering] = useState(false);
    const [currentVideoURL, setCurrentVideoURL] = useState()
    const [startTime,] = useStateWithUrlParam('t', undefined)
    const uploadedData = useUploadedDataStore(state => state.uploadedData);
    const canvasRef = useRef();
    const videoRef = useRef();

    const data = item?.data;

    const [, setError] = useState(false);
    const [selectedTab, setSelectedTab] = useStateWithUrlParam('st', 'narrations');

    const dimensions = data?.quick_info?.metadata?.dimensions.slice(1, -1).split(",").map(x => x.trim()).map(parseFloat);

    useEffect(() => {
        const available_tabs = Object.keys(data?.annotations ?? ['']).sort(keyCompare);
        const update_selected_tab = !selectedTab || (available_tabs && !available_tabs.includes(selectedTab));
        const proposed_tab = available_tabs[0];
        update_selected_tab && proposed_tab && setSelectedTab(proposed_tab);
        !currentVideoURL && setCurrentVideoURL(data?._presigned_url)
    }, [data])

    const annotation_trees = React.useMemo(() => {
        return data && <>
            <h3>Info:</h3>
            <JsonSpeedViewer data={data.quick_info} customRenderer={CustomLabelRenderer} videoRef={videoRef} setPlaying={setPlaying} videoOffset={data['_video_offset']} />

            <h3>Annotations:</h3>
            <Tabs selectedTabId={selectedTab} onChange={setSelectedTab} animate={true}>
                {
                    Object.keys(data.annotations).sort(keyCompare).map((k) =>
                        <Tab id={k} title={k} key={k} panel={
                            <div style={{ display: 'flex', height: '100%' }}>
                                <JsonSpeedViewer data={data.annotations[k]} customRenderer={CustomLabelRenderer} videoRef={videoRef} setPlaying={setPlaying} videoOffset={data['_video_offset']} expandThreshold={26} />
                            </div>
                        } />
                    )
                }
                {
                    Object.keys(uploadedData).filter(k => !!uploadedData[k][data._uid]).map(k =>
                        <Tab id={k} title={k} panel={
                            <div style={{ display: 'flex', height: '100%' }}>
                                <JsonSpeedViewer data={uploadedData[k][data._uid]} customRenderer={CustomLabelRenderer} videoRef={videoRef} setPlaying={setPlaying} videoOffset={data['_video_offset']} expandThreshold={26} />
                            </div>
                        } />
                    )
                }
            </Tabs>
        </>
    }, [selectedTab, data, uploadedData])

    // TODO: simplify this logic
    const annotations = data?.annotations[selectedTab] ?? (!!uploadedData[selectedTab] && uploadedData[selectedTab][data._uid]);

    useBBoxes({ annotations: annotations, videoRef, canvasRef, dimensions, selectedTab });

    // const videoModules = null;
    const videoModules = annotations && <VideoModules data={data} annotations={annotations} progress={progress} videoRef={videoRef} setPlaying={setPlaying} duration={duration || data._duration} />;

    const multicam_videos = data?.multicam_s3_paths && <div className="multicam-videos-container">
        {
            Object.keys(data.multicam_s3_paths).map((cam_id) =>
                <div className={"multicam-video" + (currentVideoURL === data.multicam_s3_paths[cam_id] ? ' active' : '')}
                    onClick={_event => setProgressCheckpoint(videoRef.current.getCurrentTime() || progress) || setCurrentVideoURL(data.multicam_s3_paths[cam_id])}
                    onFocus={_event => setProgressCheckpoint(videoRef.current.getCurrentTime() || progress) || setCurrentVideoURL(data.multicam_s3_paths[cam_id])}
                    onKeyDown={_event => setProgressCheckpoint(videoRef.current.getCurrentTime() || progress) || setCurrentVideoURL(data.multicam_s3_paths[cam_id])}
                    role="button"
                    key={cam_id}
                    tabIndex={-1}
                >
                    <p className="multicam-video-label">{cam_id}</p>
                    <video
                        className="multicam-video-video"
                        src={data.multicam_s3_paths[cam_id]}
                        // onMouseEnter={event => setCurrentVideoURL(data.multicam_s3_paths[cam_id])}
                        // onMouseLeave={event => event.target.pause()}
                        // onFocus={event => event.target.play()}
                        // onBlur={event => event.target.pause()}
                        muted="muted">
                        <track kind="captions" srcLang="en" />
                    </video>
                </div>
            )
        }
    </div>

    const renderedItem = data && (
        <>
            <div className="app-container">
                <div className="video-viewer">
                    <div style={{ display: "flex" }}>
                        <div style={{ width: "100%"  }}>
                            <div className="video-stage-container">
                                <div className="video-stage">
                                    <ReactPlayer
                                        className="video-player"
                                        url={currentVideoURL}
                                        controls
                                        playing={playing}
                                        ref={videoRef}
                                        width={'100%'}
                                        height={'auto'}
                                        progressInterval={350}
                                        onReady={() => {
                                            // Just once, seek to passed-in start time
                                            !playerReady && !!startTime && videoRef.current.seekTo(startTime, 'seconds');
                                            setPlayerReady(true);

                                            // If we just changed cameras, seek to the right point
                                            if (!(progressCheckpoint === null) && Math.abs(videoRef.current.getCurrentTime() - progressCheckpoint) > 0.35) {
                                                videoRef.current.seekTo(progressCheckpoint, 'seconds');
                                                setProgressCheckpoint(null);
                                            }
                                        }}
                                        onProgress={({ playedSeconds }) => {
                                            setProgress(playedSeconds);
                                        }}
                                        onDuration={setDuration}
                                        onError={(error) => {
                                            console.log(error);
                                            setError(true);
                                        }}
                                        onPlay={() => setPlaying(true)}
                                        onPause={() => setPlaying(false)}
                                        onBuffer={() => setBuffering(true)}
                                        onBufferEnd={() => setBuffering(false)}
                                        onSeek={(sec) => {
                                            setProgress(sec);
                                        }}
                                    />

                                    <ResponsiveCanvas className={"video-canvas"} ref={canvasRef} reactPlayerRef={videoRef} scale={2} playerReady={playerReady} currentVideoURL={currentVideoURL} />
                                </div>
                            </div>

                            <VideoControls videoRef={videoRef} progress={progress} id={id} rrd_path={data.rrd_path} />

                            {videoModules}
                        </div>
                        {multicam_videos}
                    </div>
                </div>
                <div className="segment-viewer">
                    {/* {concurrent_videos} */}
                    {annotation_trees}
                </div>
            </div>
        </>
    );

    return isLoading ? (
        <div className="item-dynamic">
            <ErrorPane error={error} />
            <h1 className="item-view-message">Loading...</h1>
        </div>
    ) : !!data ? (
        <>
            <ErrorPane error={error} />
            {renderedItem}
        </>
    ) : null;
}
